import { useEffect } from 'react';

// ============================================================================
// SINGLETON WAKE LOCK MANAGER (module-level, persists across component mounts)
// ============================================================================
let wakeLockInstance = null;
let wakeLockSupported = null; // null = unknown, true/false = tested
let instanceCount = 0;
let hasLoggedAcquire = false;
let hasLoggedRelease = false;
let reacquireTimeout = null;
let releaseHandler = null;
let acquiring = false; // Guard against concurrent acquisition

const REACQUIRE_DEBOUNCE_MS = 3000;

const acquireWakeLock = async () => {
    // Guard against concurrent calls
    if (acquiring) {
        return false;
    }

    // Skip if already held
    if (wakeLockInstance && !wakeLockInstance.released) {
        return true;
    }

    // Check support - must be in secure context (HTTPS)
    if (wakeLockSupported === false) {
        return false;
    }

    // Check for HTTPS requirement and API availability
    const isSecureContext = typeof window !== 'undefined' && window.isSecureContext;
    if (!isSecureContext || !('wakeLock' in navigator)) {
        wakeLockSupported = false;
        if (!hasLoggedAcquire) {
            if (!isSecureContext) {
                console.log("WakeLock: Requires HTTPS (secure context)");
            } else {
                console.log("WakeLock: Not supported in this browser");
            }
            hasLoggedAcquire = true;
        }
        return false;
    }

    acquiring = true;
    try {
        // Clean up old instance
        if (wakeLockInstance) {
            if (releaseHandler) {
                wakeLockInstance.removeEventListener('release', releaseHandler);
                releaseHandler = null;
            }
            wakeLockInstance = null;
        }

        wakeLockInstance = await navigator.wakeLock.request('screen');
        wakeLockSupported = true;
        hasLoggedRelease = false; // Reset so we can log next release

        if (!hasLoggedAcquire) {
            console.log("WakeLock: Acquired");
            hasLoggedAcquire = true;
        }

        // Single release handler
        releaseHandler = () => {
            // Only log once per release cycle
            if (!hasLoggedRelease) {
                console.log("WakeLock: Released (will reacquire when visible)");
                hasLoggedRelease = true;
            }
            wakeLockInstance = null;

            // Schedule reacquisition if page is visible
            if (document.visibilityState === 'visible' && instanceCount > 0) {
                scheduleReacquire();
            }
        };

        wakeLockInstance.addEventListener('release', releaseHandler);
        return true;
    } catch (err) {
        // NotAllowedError is expected when page not visible - don't log
        // Also catch cases where browser reports support but fails (e.g., some Android TV)
        if (err.name !== 'NotAllowedError') {
            console.log("WakeLock: Request failed -", err.message);
            wakeLockSupported = false;
        }
        return false;
    } finally {
        acquiring = false;
    }
};

const scheduleReacquire = () => {
    if (reacquireTimeout) return;

    reacquireTimeout = setTimeout(async () => {
        reacquireTimeout = null;
        if (document.visibilityState === 'visible' && instanceCount > 0) {
            await acquireWakeLock();
        }
    }, REACQUIRE_DEBOUNCE_MS);
};

const handleVisibilityChange = async () => {
    if (document.visibilityState === 'visible' && instanceCount > 0) {
        // Clear pending reacquire
        if (reacquireTimeout) {
            clearTimeout(reacquireTimeout);
            reacquireTimeout = null;
        }
        await acquireWakeLock();
    }
};

// Add visibility listener once at module level
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
}

// ============================================================================
// REACT COMPONENT
// Uses Wake Lock API only - no fallback for unsupported browsers
// ============================================================================
const WakeLockVideo = () => {
    useEffect(() => {
        instanceCount++;

        const init = async () => {
            await acquireWakeLock();
        };

        init();

        return () => {
            instanceCount--;

            // Only release wake lock when ALL instances are gone
            if (instanceCount === 0) {
                if (reacquireTimeout) {
                    clearTimeout(reacquireTimeout);
                    reacquireTimeout = null;
                }
                if (wakeLockInstance) {
                    if (releaseHandler) {
                        wakeLockInstance.removeEventListener('release', releaseHandler);
                    }
                    wakeLockInstance.release().catch(() => {});
                    wakeLockInstance = null;
                    console.log("WakeLock: Released (all components unmounted)");
                }
            }
        };
    }, []);

    // Handle user interaction to acquire wake lock (some browsers require user gesture)
    useEffect(() => {
        const handleInteraction = async () => {
            await acquireWakeLock();
        };

        document.addEventListener('click', handleInteraction, { once: true });
        document.addEventListener('touchstart', handleInteraction, { once: true });
        document.addEventListener('keydown', handleInteraction, { once: true });

        return () => {
            document.removeEventListener('click', handleInteraction);
            document.removeEventListener('touchstart', handleInteraction);
            document.removeEventListener('keydown', handleInteraction);
        };
    }, []);

    // No visual element needed - wake lock is managed via API only
    return null;
};

export default WakeLockVideo;
